<template>
  <div>
    <v-card outlined persistent>
      <v-list two-line>
        <v-list-item>
          <v-list-item-avatar v-if="loading">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
          </v-list-item-avatar>
          <v-list-item-icon v-else>
            <v-icon>mdi-account-hard-hat</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title class="title text-wrap" v-if="lead.unity">
              {{ lead.unity.user.name }}
              <span v-if="!lead.unity.user.is_active"
                ><v-icon>mdi-lock</v-icon></span
              >
            </v-list-item-title>
            <v-list-item-title v-else>
              {{ __("Nenhuma unidade atribuída") }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ __("Unidade Responsável") }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <v-menu offset-x left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-on="on" v-bind="attrs">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <v-list>
                <ScopeProvider scope="leads.read_unities_historic">
                  <v-list-item @click="handleOpenUnityHistory()" link>
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-icon left>mdi-clock</v-icon>
                        {{ __("histórico de unidades") }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </ScopeProvider>

                <v-divider></v-divider>

                <ScopeProvider scope="leads.update_unity_automatically">
                  <v-list-item @click="handleJustifyAutomaticAssign()" link>
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-icon left>mdi-auto-fix</v-icon>
                        {{ automaticAssignText }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </ScopeProvider>

                <ScopeProvider scope="leads.update_unity_manually">
                  <v-list-item @click="openUnityDialog()" link>
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-icon left>mdi-magnify-scan</v-icon>
                        {{ __("atribuir unidade manualmente") }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </ScopeProvider>

                <v-divider></v-divider>

                <ScopeProvider scope="leads.update_unity_manually">
                  <v-list-item
                    @click="handleJustifyUnassignUnity()"
                    link
                    :class="{ 'red--text': lead.unity_id }"
                    :disabled="!lead.unity_id"
                  >
                    <v-list-item-content>
                      <v-list-item-title>
                        <v-icon :disabled="!lead.unity_id" left color="red">
                          mdi-cancel
                        </v-icon>
                        {{ __("remover unidade") }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </ScopeProvider>
              </v-list>
            </v-menu>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-card>

    <ChangeLeadUnityDialog v-model="unity_dialog" />
    <TextFieldDialog
      :title="__('mudar unidade')"
      :subtitle="__('a plataforma substituirá a unidade atual automaticamente')"
      :field-label="__('motivo')"
      :field-placeholder="__('justifique a troca de unidade')"
      :confirm-text="__('confirmar')"
      :cancelText="__('Cancelar')"
      ref="justificationDialog"
      @submit="handleAutomaticAssign($event.text)"
    ></TextFieldDialog>

    <TextFieldDialog
      :title="__('remover unidade')"
      :subtitle="__('o serviço ficará sem unidade responsável')"
      :field-label="__('motivo')"
      :field-placeholder="__('justifique a remoção')"
      :confirm-text="__('confirmar')"
      :cancelText="__('Cancelar')"
      ref="removeJustificationDialog"
      :errorMessage="errorMessage"
      @submit="(form, done) => handleUnassignUnity(form.text, done)"
      @clearErrorMessage="clearErrorMessage"
    ></TextFieldDialog>

    <LeadUnityHistoryDialog
      v-model="unityHistoryDialog"
      :items="unityHistory"
    />
  </div>
</template>

<script>
import ChangeLeadUnityDialog from "@/components/Lead/ChangeLeadUnityDialog";
import { sugestAndAssign, unassign, getHistory } from "@/services/lead_unities";
import TextFieldDialog from "../TextFieldDialog.vue";
import LeadUnityHistoryDialog from "@/components/LeadUnities/LeadUnityHistoryDialog";
import i18n from "@/mixins/i18n";
import ScopeProvider from "@/components/ScopeProvider";
import ToastsMixin from "@/mixins/ToastsMixin.vue";

export default {
  mixins: [ToastsMixin, i18n],
  name: "resume-unity-card",
  components: {
    ChangeLeadUnityDialog,
    TextFieldDialog,
    LeadUnityHistoryDialog,
    ScopeProvider,
  },
  data() {
    return {
      i18n_prefix: "schedulings",
      unity_dialog: false,
      unityHistory: [],
      unityHistoryDialog: false,
      loading: false,
      errorMessage: "",
    };
  },
  methods: {
    openUnityDialog() {
      this.unity_dialog = true;
    },

    handleJustifyAutomaticAssign() {
      if (this.lead.unity_id) {
        this.$refs.justificationDialog.init();
        return;
      }

      this.handleAutomaticAssign("");
    },

    handleJustifyUnassignUnity() {
      this.$refs.removeJustificationDialog.init();
    },

    async handleAutomaticAssign(motive) {
      this.loading = true;
      try {
        const response = await sugestAndAssign(this.lead.id, {
          motive,
        });

        if (response.assigned_unity === null) {
          this.$store.commit(
            "alert",
            "Não encontramos unidades disponíveis, atribua manualmente"
          );
          this.$store.commit("unassignUnity");
        } else {
          this.$store.commit("assignUnity", response.assigned_unity);

          this.$store.commit("alert", "Unidade atribuída automaticamente");
        }
      } catch (error) {
        if (error.response?.data?.message) {
          this.$store.commit("error", error.response.data.message);
          return;
        }

        this.$store.commit("error", "Falha ao atribuir unidade");
      } finally {
        this.loading = false;
      }
    },

    async handleUnassignUnity(motive, done) {
      this.loading = true;

      try {
        await unassign(this.lead.id, { motive });
        this.$store.commit("unassignUnity");
        this.toast(this.__("Unidade removida com sucesso", "schedulings"));

        done(true);
      } catch (error) {
        const response = error.response?.data;
        this.errorMessage =
          response?.errors?.motive?.join(" ") ||
          response?.message ||
          this.__("Falha ao remover unidade", "schedulings");

        this.toastError(this.errorMessage);
        done(false);
      } finally {
        this.loading = false;
      }
    },

    clearErrorMessage() {
      this.errorMessage = "";
    },

    async handleOpenUnityHistory() {
      this.unityHistoryDialog = true;

      const response = await getHistory(this.lead.id);
      this.unityHistory = response.data;
    },
  },
  computed: {
    lead: function () {
      return this.$store.getters.getLead;
    },
    automaticAssignText() {
      if (!this.lead.unity_id) {
        return this.__("sugerir unidade", "schedulings");
      } else {
        return this.__("sugerir outra unidade", "schedulings");
      }
    },
  },
};
</script>
